import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermission, useAnyPermission, useAllPermissions, useRole, useAnyRole, useRoleOrHigher } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import type { RolePermissions, UserRole } from '../../types';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  permission?: keyof RolePermissions;
  permissions?: (keyof RolePermissions)[];
  requireAll?: boolean;
  role?: UserRole;
  roles?: UserRole[];
  requireRoleOrHigher?: UserRole;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

/**
 * Component that protects routes based on user permissions or roles
 */
export function RoleProtectedRoute({
  children,
  permission,
  permissions,
  requireAll = false,
  role,
  roles,
  requireRoleOrHigher,
  redirectTo = '/dashboard',
  fallback,
}: RoleProtectedRouteProps) {
  // Always call all hooks to avoid conditional hook calls
  const hasPermission = usePermission(permission || 'canViewClients');
  const hasAllPermissions = useAllPermissions(permissions || []);
  const hasAnyPermission = useAnyPermission(permissions || []);
  const hasRole = useRole(role || 'receptionist');
  const hasAnyRole = useAnyRole(roles || []);
  const hasRoleOrHigher = useRoleOrHigher(requireRoleOrHigher || 'receptionist');

  // Check single permission
  if (permission) {
    if (!hasPermission) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  // Check multiple permissions
  if (permissions) {
    const hasPermissions = requireAll ? hasAllPermissions : hasAnyPermission;
    if (!hasPermissions) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  // Check single role
  if (role) {
    if (!hasRole) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  // Check multiple roles
  if (roles) {
    if (!hasAnyRole) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  // Check role hierarchy
  if (requireRoleOrHigher) {
    if (!hasRoleOrHigher) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  // If no conditions specified or all conditions met, render children
  return <>{children}</>;
}

/**
 * Access denied component for unauthorized users
 */
export function AccessDenied({ message = "You don't have permission to access this page." }: { message?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="mt-2 text-sm text-gray-500">{message}</p>
          <div className="mt-6">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Klijentli Admin Route Protection
 * Only allows access to users from Klijentli company
 */
export function KlijentliAdminRoute({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is Klijentli admin
  if (!user.isKlijentliAdmin) {
    return fallback ? <>{fallback}</> : <AccessDenied message="Klijentli admin access required." />;
  }

  return <>{children}</>;
}
